// Domain 06 activity widgets. Same contract as ./widgets.js, including verify(data, rt) for the
// server replay (src/core/labVerify.js).
//   aaaConsole  — what the AAA server (RADIUS / TACACS+) actually received, from live state
//   deskSwap    — a physical action at a desk: unplug the PC and plug in another
//                 device (the switch only ever sees a different source MAC address)
import { h } from './h.js';

/** AAA server log viewer (read-only view of the server's own records). */
export function aaaConsole({ id, server = 'SRV-AAA', title = 'AAA SERVER LOG · SRV-AAA', limit = 10, required = true }) {
  return {
    id, required, blocker: 'Refresh the AAA server log',
    verify: () => true, // "Refresh server log": the log is recomputed from the replayed network
    mount(el, rt, api) {
      const s = api.state.data;
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, s.t ? h('span', {}, `as of ${s.t}`) : null),
          h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `aaa-refresh-${id}`, onclick: () => {
            const log = rt.net.devices[server]?.aaa?.log || [];
            s.rows = log.slice(-limit).map((e) => ({ ...e }));
            s.total = log.length;
            s.t = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            rt.action('aaa-refresh', { widget: id, entries: log.length, accepts: log.filter((e) => /Accept|PASS/.test(e.result)).map((e) => `${e.proto}|${e.user}`), acct: log.filter((e) => e.type === 'acct').map((e) => `${e.proto}|${e.user}`) });
            api.done(); api.save(); render();
          } }, 'Refresh server log')));
        if (!s.rows) return;
        el.append(h('pre', { class: 'nms-log', 'data-testid': `aaa-log-${id}` }, s.rows.length
          ? ['#   Protocol  Client  User         From           Result', ...s.rows.map((e) => `${String(e.n).padEnd(4)}${(e.proto === 'radius' ? 'RADIUS' : 'TACACS+').padEnd(10)}${e.client.padEnd(8)}${String(e.user || '').padEnd(13)}${String(e.src || '').padEnd(15)}${e.result}`)].join('\n')
          : '(no requests received — the server has not heard from any device)'));
      };
      render();
    },
  };
}

/**
 * Physical change at a desk port: the learner unplugs the registered PC and plugs
 * in another device (a visitor laptop), then can put the original PC back. Only the
 * source MAC address the switch sees changes — exactly what port security reacts to.
 */
export function deskSwap({ id, host, mac, title = 'AT THE DESK', label, intro, required = true }) {
  return {
    id, required, blocker: label ? `Use “${label}”` : 'Use the desk activity',
    deskSwap: { host, mac }, // used by the 'desk-swap' operation (src/core/labOps.js)
    // done once the visitor device was really plugged in (the operation is replayed by the server)
    verify: (d, rt) => rt.state.log.some((e) => e.type === 'action' && e.name === 'desk-swap' && e.widget === id && e.plugged === 'visitor'),
    mount(el, rt, api) {
      const s = api.state.data;
      const d = () => rt.net.devices[host];
      const render = () => {
        el.textContent = '';
        s.orig ||= d().nic.mac;
        const swapped = d().nic.mac !== s.orig;
        el.append(h('div', { class: 'w-title' }, title, h('span', {}, swapped ? 'visitor laptop connected' : `${host} connected`)));
        if (intro) el.append(h('p', { class: 'small muted' }, intro));
        el.append(h('div', { class: 'row' },
          h('button', { class: 'btn small', 'data-testid': `swap-${id}`, disabled: swapped || null, onclick: () => {
            rt.op('desk-swap', { widget: id, plugged: 'visitor' }); // src/core/labOps.js (replayed by the server)
            api.done(); api.save(); render(); rt.emit('net');
          } }, label || `Unplug ${host} and plug in the visitor laptop`),
          h('button', { class: 'btn small ghost', 'data-testid': `restore-${id}`, disabled: !swapped || null, onclick: () => {
            rt.op('desk-swap', { widget: id, plugged: 'original' });
            api.save(); render(); rt.emit('net');
          } }, `Reconnect ${host}`)));
        el.append(h('p', { class: 'small' }, `The laptop now on this cable answers as ${host} would — same IP settings, different network card (MAC ${(swapped ? mac : s.orig).match(/../g).join('-')}).`));
      };
      render();
    },
  };
}
